import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAppLayoutContextConsumer } from '@occc-app/design-system';
import { useGetCampaignName, getCreatePageLinks } from '@occc-app/routes/CampaignComponents/utils';
import { Offer_Engagement_Type } from '@occc/graphql';
import { OfferType } from '@occc-app/offerValidationService/types';

import { useChainId } from '@occc-app/Chain/hooks';
import { OfferCreateConfigurePage } from '@occc-app/routes/Offer/OfferCreatePage/OfferCreateConfigurePage';
import { CampaignComponentTabs } from '@occc-app/routes/CampaignComponents/components/ConfigurationLayout';
import { Flow_Types } from '@occc-app/offerHelpers/types';
import { useMakeOffersFilter } from '@occc-app/offerHelpers/hooks/use-make-offers-filter.hook';
import { useNavigateToCampaign } from '@occc-app/offerHelpers/hooks/useNavigateToCampaign';
import { ConfigureFormInitialValues, ConfigureFormValues } from '@occc-app/routes/Reward/types';
import {
  useOfferReturnDefaultStrategy,
  useOfferReturnStrategy,
} from '@occc-app/Chain/hooks/useOfferReturnStrategy';
import { messages } from '../messages';
import { routeCreator } from '../utils';

import { ConfigureForm } from '../components/RewardConfigureForm/ConfigureForm';
import { getConfigureFormOperations } from '../components/RewardConfigureForm/utils/configureFormOperations';
import { getTransformOfferToInitialValues } from '../components/RewardConfigureForm/utils/configureFormInitialValues';

export const RewardCreatePageContainer = () => {
  const navigate = useNavigate();
  const { id: campaignId = '' } = useParams<{ id: string }>();

  const chainId = useChainId();
  const campaignName = useGetCampaignName(chainId, campaignId);
  const navigateToCampaign = useNavigateToCampaign(campaignId);

  const links = useMemo(
    () => getCreatePageLinks(campaignId, campaignName, 'New Reward'),
    [campaignId, campaignName],
  );
  useAppLayoutContextConsumer(links);

  const offerReturnStrategy = useOfferReturnStrategy();
  const offerReturnDefaultStrategy = useOfferReturnDefaultStrategy();
  const makeOffersFilter = useMakeOffersFilter();

  return (
    <OfferCreateConfigurePage<ConfigureFormValues, ConfigureFormInitialValues>
      pageTitle='Campaign > Create'
      subtitle={messages.subtitle}
      offerType={OfferType.Reward}
      engagementType={Offer_Engagement_Type.Purchaseable}
      campaignId={campaignId}
      getConfigureOperations={getConfigureFormOperations}
      getInitialValues={getTransformOfferToInitialValues({
        offerReturnStrategy,
        offerReturnDefaultStrategy,
      })}
      onOfferCreated={(offerId) =>
        navigate(routeCreator.configureWithTab(campaignId, offerId, CampaignComponentTabs.Target))
      }
      renderConfigureForm={({ onSubmit, initialValues, loading }) => (
        <ConfigureForm
          onSubmit={onSubmit}
          initialValues={initialValues}
          loading={loading}
          onCancelClick={navigateToCampaign}
          makeOffersFilter={makeOffersFilter}
          currentFlowType={Flow_Types.Configure}
          isFormEditable
        />
      )}
    />
  );
};
